/* eslint-disable jsx-a11y/click-events-have-key-events */
import { forwardRef, ComponentPropsWithoutRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Text } from '@components/Text';
import { FLEET_IMAGES_URL } from '@constants';
import { VehicleSummary } from '@views/FleetMaster/types';
import {
  StyledFleetList,
  StyledFleetLoading,
  StyledFleetLoadingIndicator,
} from './FleetList.styles';

interface FleetListProps extends ComponentPropsWithoutRef<'div'> {
  id?: string;
}

const fleetImagePlaceholder = `/images/vehicle_placeholder.png`;

export const FleetList = forwardRef<HTMLDivElement, FleetListProps>(
  ({ children, id = 'fleet-list' }, forwardedRef) => {
    return (
      <StyledFleetList id={id} ref={forwardedRef}>
        {children}
      </StyledFleetList>
    );
  }
);

FleetList.displayName = 'FleetList';

interface FleetCardProps extends ComponentPropsWithoutRef<'div'> {
  vehicleSummary: VehicleSummary;
  orientation: 'vertical' | 'horizontal';
  onClick: () => void;
}

export const FleetCard = forwardRef<HTMLDivElement, FleetCardProps>(
  ({ vehicleSummary, orientation, onClick, ...props }, forwardedRef) => {
    const hasActualImage =
      vehicleSummary.images && vehicleSummary.images.length > 0;
    const imageUrl = hasActualImage
      ? `${FLEET_IMAGES_URL}/${vehicleSummary.images[0].imageLocation}/small/${vehicleSummary.images[0].remoteName}`
      : fleetImagePlaceholder;

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        className='group relative flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-gray-300 w-[220px]'
        onClick={onClick}
        {...props}
        ref={forwardedRef}
      >
        {/* Image container */}
        <div className='relative h-[140px] bg-gray-50'>
          <img
            src={imageUrl}
            alt={vehicleSummary.model}
            className={`w-full h-full ${
              hasActualImage ? 'object-cover' : 'object-contain p-4'
            }`}
          />
          {/* Action buttons overlay */}
          {/* <div className='absolute top-2 right-2 flex items-center gap-1.5'>
            <button
              type='button'
              className='w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors'
              onClick={(e) => {
                e.stopPropagation();
                // Calendar action
              }}
            >
              <MdDateRange className='w-4 h-4 text-gray-600' />
            </button>
            <button
              type='button'
              className='w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 transition-colors'
              onClick={(e) => {
                e.stopPropagation();
                // More actions menu
              }}
            >
              <MdMoreHoriz className='w-4 h-4 text-gray-600' />
            </button>
          </div> */}
        </div>

        {/* Info section */}
        <div className='flex flex-col gap-1.5 p-3 border-t border-gray-100'>
          <h3 className='text-base font-semibold text-gray-900 truncate'>
            {vehicleSummary.model}
          </h3>
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <span className='font-medium'>#</span>
            <span className='truncate'>
              {vehicleSummary.registrationNumber}
            </span>
          </div>
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <svg
              className='w-4 h-4'
              viewBox='0 0 14 14'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <rect
                x='1'
                y='3'
                width='12'
                height='8'
                rx='1'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <circle cx='4' cy='11' r='1.5' fill='currentColor' />
              <circle cx='10' cy='11' r='1.5' fill='currentColor' />
            </svg>
            <span>
              {vehicleSummary?.vehicleCapacity?.maxKilograms?.toLocaleString()}{' '}
              kg
            </span>
          </div>
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <svg
              className='w-4 h-4'
              viewBox='0 0 14 14'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <rect
                x='1'
                y='1'
                width='5'
                height='5'
                rx='0.5'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <rect
                x='8'
                y='1'
                width='5'
                height='5'
                rx='0.5'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <rect
                x='1'
                y='8'
                width='5'
                height='5'
                rx='0.5'
                stroke='currentColor'
                strokeWidth='1.5'
              />
              <rect
                x='8'
                y='8'
                width='5'
                height='5'
                rx='0.5'
                stroke='currentColor'
                strokeWidth='1.5'
              />
            </svg>
            <span>
              {vehicleSummary?.vehicleCapacity?.maxPallets} standard pallets
            </span>
          </div>
        </div>
      </div>
    );
  }
);

FleetCard.displayName = 'FleetCard';

interface EmptyFleetCardProps {
  vehicleType: string;
  displayName: string;
  onClick: () => void;
}

export function EmptyFleetCard({
  vehicleType,
  displayName,
  onClick,
}: EmptyFleetCardProps) {
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className='group relative flex flex-col items-center justify-center bg-white border-2 border-dashed border-gray-300 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:border-amber-400 hover:shadow-md w-[220px] h-[268px]'
      onClick={onClick}
    >
      <ExclamationTriangleIcon className='w-10 h-10 text-amber-400 mb-3' />
      <p className='text-sm font-semibold text-gray-700'>Not configured</p>
      <p className='text-xs text-gray-400 mt-1 px-4 text-center'>
        Click to add {displayName} details
      </p>
    </div>
  );
}

EmptyFleetCard.displayName = 'EmptyFleetCard';

export function FleetLoading() {
  return (
    <StyledFleetLoading>
      <StyledFleetLoadingIndicator />
      <Text
        size='xl'
        weight='bold'
        css={{ color: 'inherit', textAlign: 'center' }}
      >
        Fetching Fleet details...
      </Text>
    </StyledFleetLoading>
  );
}

FleetLoading.displayName = 'FleetLoading';
